import { useCallback, useEffect, useMemo, useState } from 'react';
import { useResponsive } from '../context/ResponsiveContext';

const STORAGE_KEY = 'kl_sidebar_collapsed';

export const SIDEBAR_WIDTH_EXPANDED = 216;
export const SIDEBAR_WIDTH_COLLAPSED = 64;

function readStoredCollapsed() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    return raw === 'true';
  } catch {
    return null;
  }
}

/**
 * Responsive sidebar layout state.
 * - Mobile (<768px): hamburger drawer, no rail
 * - Tablet (768–1023px): collapsed icon rail, no hamburger
 * - Desktop (≥1024px): expandable sidebar
 * Breakpoints come from ResponsiveContext — do not duplicate media queries here.
 */
export function useSidebarState() {
  const { isMobile, isTablet, isDesktop } = useResponsive();

  const mode = isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet';

  const [storedCollapsed, setStoredCollapsed] = useState(readStoredCollapsed);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const isCollapsed = useMemo(() => {
    if (mode === 'mobile') return true;
    if (mode === 'tablet') return true;
    if (storedCollapsed !== null) return storedCollapsed;
    return false;
  }, [mode, storedCollapsed]);

  const effectiveWidth =
    mode === 'mobile' ? 0 : isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  const toggleCollapsed = useCallback(() => {
    if (mode !== 'desktop') return;
    setStoredCollapsed((prev) => {
      const current = prev ?? false;
      const next = !current;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [mode]);

  const expandSidebar = useCallback(() => {
    if (mode !== 'desktop') return;
    setStoredCollapsed(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, 'false');
    } catch {
      /* ignore */
    }
  }, [mode]);

  const openMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(true);
  }, []);

  const closeMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    html.classList.remove(
      'sidebar-layout-mobile',
      'sidebar-layout-collapsed',
      'sidebar-layout-expanded'
    );
    if (effectiveWidth === 0) {
      html.classList.add('sidebar-layout-mobile');
    } else if (effectiveWidth === SIDEBAR_WIDTH_COLLAPSED) {
      html.classList.add('sidebar-layout-collapsed');
    } else {
      html.classList.add('sidebar-layout-expanded');
    }
    return () => {
      html.classList.remove(
        'sidebar-layout-mobile',
        'sidebar-layout-collapsed',
        'sidebar-layout-expanded'
      );
    };
  }, [effectiveWidth]);

  useEffect(() => {
    if (mode !== 'mobile' && mobileDrawerOpen) {
      setMobileDrawerOpen(false);
    }
  }, [mode, mobileDrawerOpen]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (mode !== 'mobile' || !mobileDrawerOpen) return;

    document.body.classList.add('drawer-scroll-lock');
    return () => {
      document.body.classList.remove('drawer-scroll-lock');
    };
  }, [mode, mobileDrawerOpen]);

  return {
    mode,
    isMobile,
    isTablet,
    isCollapsed,
    effectiveWidth,
    isRail: mode === 'tablet' || (mode === 'desktop' && isCollapsed),
    isExpanded: mode === 'desktop' && !isCollapsed,
    toggleCollapsed,
    expandSidebar,
    mobileDrawerOpen,
    setMobileDrawerOpen,
    openMobileDrawer,
    closeMobileDrawer,
  };
}

export default useSidebarState;
